import React, { useEffect, useState, useMemo } from "react";
import PageHeader from "../../../common/pageheader/pageheader";
import AGTable from "../../../common/table/AGTable";
import api from "../../../../resources/api";
import Loader from "../../../common/loader/loader";
import { currencyConverter, formatDateAndTime } from "../../../../resources/constants";
import { connect } from "react-redux";
import SearchSelect from "../../../common/select/SearchSelect";

const today = () => new Date().toISOString().split("T")[0];
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().split("T")[0]; };

const METHOD_OPTIONS = [
    { value: "", label: "All Methods" },
    { value: "Card", label: "Card" },
    { value: "BankTransfer", label: "Bank Transfer" },
    { value: "Wallet", label: "Wallet" },
    { value: "Mixed", label: "Mixed" },
];

function CollectionRegister() {
    const [isLoading, setIsLoading] = useState(false);
    const [reportData, setReportData] = useState([]);
    const [summaryStats, setSummaryStats] = useState({ TotalTransactions: 0, TotalCollected: 0, WalletTotal: 0, GatewayTotal: 0 });
    const [filters, setFilters] = useState({ from_date: daysAgo(30), to_date: today(), PaymentMethod: "" });
    const [datatable, setDatatable] = useState({
        columns: [
            { label: "S/N", field: "sn" },
            { label: "Receipt No", field: "ReceiptNo" },
            { label: "Credo Ref", field: "CredoRef" },
            { label: "Student ID", field: "StudentID" },
            { label: "Student Name", field: "StudentName" },
            { label: "Invoice No", field: "InvoiceNo" },
            { label: "Amount", field: "Amount" },
            { label: "Wallet Amt", field: "WalletAmount" },
            { label: "Gateway Amt", field: "GatewayAmount" },
            { label: "Method", field: "Method" },
            { label: "Paid At", field: "PaidAt" },
        ],
        rows: [],
    });

    const getReportData = async () => {
        if (!filters.from_date || !filters.to_date) return;
        setIsLoading(true);
        let endpoint = `staff/finance/invoices/collection-register?from_date=${filters.from_date}&to_date=${filters.to_date}`;
        if (filters.PaymentMethod) endpoint += `&method=${filters.PaymentMethod}`;
        const result = await api.get(endpoint);
        if (result.success && result.data?.data) {
            const { summary, rows } = result.data.data;
            setSummaryStats(summary || {});
            setReportData(rows || []);
            buildTable(rows || []);
        }
        setIsLoading(false);
    };

    const buildTable = (data) => {
        const rows = data.map((item, index) => ({
            sn: index + 1,
            ReceiptNo: <span className="fw-bold text-primary">{item.ReceiptNumber}</span>,
            CredoRef: item.CredoReference === "N/A"
                ? <span className="text-muted fst-italic">N/A</span>
                : <span className="text-dark">{item.CredoReference}</span>,
            StudentID: item.StudentID,
            StudentName: <span className="fw-bold">{item.StudentName}</span>,
            InvoiceNo: <span className="text-muted">#{item.InvoiceNumber}</span>,
            Amount: <span className="text-success fw-bold">{currencyConverter(item.Amount)}</span>,
            WalletAmount: <span className="text-info">{currencyConverter(item.WalletAmount)}</span>,
            GatewayAmount: <span>{currencyConverter(item.GatewayAmount)}</span>,
            Method: <span className="badge badge-light-info">{item.PaymentMethod}</span>,
            PaidAt: formatDateAndTime(item.PaidAt, "date_and_time"),
        }));
        setDatatable(prev => ({ ...prev, rows }));
    };

    const exportToCSV = () => {
        if (!reportData.length) return;
        const headers = ["Receipt No", "Credo Ref", "Student ID", "Student Name", "Invoice No", "Amount", "Wallet Amt", "Gateway Amt", "Method", "Paid At"];
        const rows = reportData.map(item => [
            item.ReceiptNumber, item.CredoReference, item.StudentID, item.StudentName,
            item.InvoiceNumber, item.Amount, item.WalletAmount, item.GatewayAmount,
            item.PaymentMethod, formatDateAndTime(item.PaidAt, "date_and_time"),
        ]);
        const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(",")).join("\n");
        const a = document.createElement("a");
        a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
        a.download = `collection_register_${filters.from_date}_to_${filters.to_date}.csv`;
        a.click();
    };

    const onFilterChange = (e) => setFilters(f => ({ ...f, [e.target.id]: e.target.value }));

    const statCards = [
        { label: "Total Transactions", value: summaryStats.TotalTransactions || 0, color: "primary", icon: "fa-receipt", isCount: true },
        { label: "Total Collected", value: summaryStats.TotalCollected || 0, color: "success", icon: "fa-money-bill-wave" },
        { label: "Wallet Total", value: summaryStats.WalletTotal || 0, color: "info", icon: "fa-wallet" },
        { label: "Gateway Total", value: summaryStats.GatewayTotal || 0, color: "warning", icon: "fa-credit-card" },
    ];

    return isLoading ? <Loader /> : (
        <div className="d-flex flex-column flex-row-fluid">
            <PageHeader title="Collection Register" items={["Finance", "Invoices", "Collection Register"]} />
            <div className="flex-column-fluid">
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row g-3 align-items-end">
                            <div className="col-md-3">
                                <label className="form-label required">From Date</label>
                                <input type="date" id="from_date" className="form-control" value={filters.from_date} onChange={onFilterChange} />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label required">To Date</label>
                                <input type="date" id="to_date" className="form-control" value={filters.to_date} onChange={onFilterChange} />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">Payment Method</label>
                                <SearchSelect
                                    id="PaymentMethod"
                                    value={METHOD_OPTIONS.find(o => o.value === filters.PaymentMethod) || METHOD_OPTIONS[0]}
                                    options={METHOD_OPTIONS}
                                    onChange={(s) => onFilterChange({ target: { id: "PaymentMethod", value: s?.value || "" } })}
                                    placeholder="All Methods"
                                    isClearable={false}
                                />
                            </div>
                            <div className="col-md-3 d-flex gap-2 justify-content-end">
                                <button className="btn btn-primary" onClick={getReportData}>
                                    <i className="fa fa-search me-2"></i>Generate
                                </button>
                                <button className="btn btn-light-primary" onClick={exportToCSV} disabled={!reportData.length}>
                                    <i className="fa fa-download me-2"></i>CSV
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {reportData.length > 0 && (
                    <>
                        <div className="row g-4 mb-4">
                            {statCards.map((c, i) => (
                                <div className="col-md-3" key={i}>
                                    <div className={`card bg-light-${c.color}`}>
                                        <div className="card-body py-4">
                                            <div className="d-flex align-items-center">
                                                <div className="symbol symbol-50px me-3">
                                                    <span className={`symbol-label bg-${c.color}`}>
                                                        <i className={`fa ${c.icon} text-white fs-3`}></i>
                                                    </span>
                                                </div>
                                                <div>
                                                    <div className="fs-4 fw-bold">{c.isCount ? c.value : currencyConverter(c.value)}</div>
                                                    <div className="text-muted small">{c.label}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="card">
                            <div className="card-header border-0 pt-5">
                                <h3 className="card-title">Payment Transactions</h3>
                                <div className="card-toolbar">
                                    <span className="text-muted fs-7">{reportData.length} records</span>
                                </div>
                            </div>
                            <div className="card-body py-3">
                                <AGTable data={datatable} />
                            </div>
                        </div>
                    </>
                )}

                {!reportData.length && !isLoading && (
                    <div className="card">
                        <div className="card-body text-center py-15">
                            <i className="fa fa-file-invoice-dollar fs-2x text-muted mb-4"></i>
                            <p className="text-muted fs-5">Select a date range and click Generate to view the collection register</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default connect()(CollectionRegister);
